// Copyright (c) Microsoft. All rights reserved.

import React from 'react';
import { Button, makeStyles, mergeClasses, shorthands } from '@fluentui/react-components';
import { Drawer, DrawerBody, DrawerHeader, DrawerHeaderTitle } from '@fluentui/react-drawer';
import { ArrowExpand16Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { useChat } from '../../../libs/hooks/useChat';
import { IChatSearchQuery } from '../../../libs/models/ChatMessage';

interface JsonTableProps {
    jsonData: Array<Record<string, string>>;
    showMoreDetails?: boolean;
}

export interface IArchiveItem {
    ID: string;
    Type: string;
}

const useClasses = makeStyles({
    archiveTable: {
        '& > table': {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '10px',
            marginBottom: '20px',
            '& th': {
                backgroundColor: 'rgb(25, 133, 151)',
                color: 'white',
                textAlign: 'left',
                ...shorthands.borderBottom('1px', 'solid', '#eee'),
                ...shorthands.padding('8px'),
            },
            '& tr:last-child th': {
                ...shorthands.borderRadius('0px', '0px', '0px', '5px'),
            },
            '& tr:last-child td': {
                ...shorthands.borderRadius('0px', '0px', '5px', '0px'),
            },
            '& td': {
                backgroundColor: '#cccccc',
                ...shorthands.borderBottom('1px', 'solid', '#eeeeee'),
                ...shorthands.padding('8px'),
            },
            '& td.firstHeader': {
                ...shorthands.padding('0', '0', '8px', '0'),
            },
            '& td.header': {
                ...shorthands.padding('30px', '0', '8px', '0'),
            },
        },
        '& > table:nth-child(2)': {
            marginTop: '10px',
            marginBottom: '20px',
        },
    },
    archiveDataLinkWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    archiveDataLink: {
        fontWeight: '600',
        textDecorationLine: 'underline',
        color: 'rgb(15, 101, 110)',
        cursor: 'pointer',
        ':hover': {
            color: 'rgb(188, 98, 126)',
        },
    },
    archiveDataLinkIcon: {
        display: 'flex',
    },
});

export const JsonTable: React.FC<JsonTableProps> = ({ jsonData, showMoreDetails }) => {
    const classes = useClasses();

    const chat = useChat();

    const [isOpen, setIsOpen] = React.useState(false);
    const [selectedItem, setSelectedItem] = React.useState<IArchiveItem>();
    const [archiveMemoryDetails, setArchiveMemoryDetails] = React.useState<Array<Record<string, string>>>([]);

    const getArchiveMemoryDetails = async () => {
        const searchQuery: IChatSearchQuery = {
            query: '',
            filter: `SystemID eq '${selectedItem?.ID}'`,
            useSemanticSearch: false,
            semanticConfigName: '',
        };

        const archiveMemoryDetailsResponse = await chat.getArchiveMemoryDetails(searchQuery);

        if (archiveMemoryDetailsResponse.length > 0) {
            const archiveMemoryDetailsJson = archiveMemoryDetailsResponse[0]
                .replaceAll('\\"', '&qoute;')
                .replaceAll('\\', '\\\\');

            const archiveMemoryDetailsArray = JSON.parse(archiveMemoryDetailsJson) as Array<Record<string, string>>;
            setArchiveMemoryDetails(archiveMemoryDetailsArray);
        } else {
            setArchiveMemoryDetails([]);
        }
    };

    React.useEffect(() => {
        if (isOpen) {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            getArchiveMemoryDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    if (jsonData.length === 0) {
        return null;
    }

    if (jsonData.length === 1 && jsonData[0].NoDataMessage) {
        return <div>{jsonData[0].NoDataMessage}</div>;
    }

    return (
        <div className={classes.archiveTable}>
            <table>
                <tbody>
                    {jsonData.map((item, index) => (
                        <React.Fragment key={index}>
                            {item.Type && showMoreDetails && (
                                <tr>
                                    <td
                                        colSpan={2}
                                        className={index === 0 ? 'firstHeader' : 'header'}
                                        style={{
                                            background: 'white',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {`${item.Type}: ${item['System ID']}`}
                                    </td>
                                </tr>
                            )}
                            {Object.keys(item)
                                .filter((k) => k !== 'Type')
                                .map((key, idx) => (
                                    <tr key={idx}>
                                        <th style={idx === 0 ? { borderRadius: '5px 0 0 0' } : {}}>{key}</th>
                                        {key === 'System ID' ? (
                                            <td
                                                title="View more details"
                                                style={idx === 0 ? { borderRadius: '0 5px 0 0' } : {}}
                                            >
                                                <div className={classes.archiveDataLinkWrapper}>
                                                    <div
                                                        className={showMoreDetails ? classes.archiveDataLink : ''}
                                                        onClick={
                                                            showMoreDetails
                                                                ? () => {
                                                                      setSelectedItem({
                                                                          ID: item['System ID'],
                                                                          Type: item.Type,
                                                                      });
                                                                      setIsOpen(!isOpen);
                                                                  }
                                                                : () => {}
                                                        }
                                                    >
                                                        {item[key]}
                                                    </div>
                                                    {showMoreDetails && (
                                                        <div
                                                            className={mergeClasses(
                                                                classes.archiveDataLink,
                                                                classes.archiveDataLinkIcon,
                                                            )}
                                                            onClick={() => {
                                                                setSelectedItem({
                                                                    ID: item['System ID'],
                                                                    Type: item.Type,
                                                                });
                                                                setIsOpen(!isOpen);
                                                            }}
                                                        >
                                                            <ArrowExpand16Regular />
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        ) : (
                                            <td style={idx === 0 ? { borderRadius: '0 5px 0 0' } : {}}>{item[key]}</td>
                                        )}
                                    </tr>
                                ))}
                        </React.Fragment>
                    ))}
                </tbody>
            </table>
            <Drawer
                type="overlay"
                position="end"
                size="large"
                separator
                open={isOpen}
                onOpenChange={(_, { open }) => {
                    setIsOpen(open);
                }}
            >
                <DrawerHeader>
                    <DrawerHeaderTitle
                        action={
                            <Button
                                appearance="subtle"
                                aria-label="Close"
                                icon={<Dismiss24Regular />}
                                onClick={() => {
                                    setIsOpen(false);
                                }}
                            />
                        }
                    >
                        {`Details${selectedItem?.Type ? ` of ${selectedItem.Type}:` : ':'} ${selectedItem?.ID}`}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    {archiveMemoryDetails.length > 0 ? (
                        <JsonTable jsonData={archiveMemoryDetails} />
                    ) : (
                        <div>No details found</div>
                    )}
                </DrawerBody>
            </Drawer>
        </div>
    );
};
